import React, { useState, useEffect, useContext } from 'react';
import { 
  ExclamationTriangleIcon, 
  XMarkIcon, 
  CheckIcon,
  ClockIcon,
  BanknotesIcon,
  DocumentTextIcon,
  EyeIcon,
  PlusIcon,
  PhotoIcon,
  UserIcon,
  HomeIcon,
  CalendarIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { AuthContext } from './AuthContext';
import ViolationSettings from './components/ViolationSettings';
import api from './api';
import { useMultiHOA } from './MultiHOAContext';

const ViolationSystem = () => {
  const { user } = useContext(AuthContext);
  const multiHOA = useMultiHOA();
  const activeHoaId = multiHOA?.activeHoaId || user?.hoa_id;
  const [violations, setViolations] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showFineModal, setShowFineModal] = useState(false);
  const [showHearingModal, setShowHearingModal] = useState(false);
  const [showFineUpdateModal, setShowFineUpdateModal] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [fineAmount, setFineAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  // Enhanced filtering states
  const [searchQuery, setSearchQuery] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [upcomingPaymentFilter, setUpcomingPaymentFilter] = useState('');
  
  // Comment system states
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [commentAttachments, setCommentAttachments] = useState([]);

  const violationTypes = [
    'landscaping', 'parking', 'architectural', 'noise', 'pets', 
    'trash', 'maintenance', 'unauthorized_changes', 'other'
  ];

  const violationSeverities = {
    minor: { label: 'Minor', color: 'bg-yellow-100 text-yellow-800', description: 'Warning only' },
    moderate: { label: 'Moderate', color: 'bg-orange-100 text-orange-800', description: 'Warning then fine' },
    severe: { label: 'Severe', color: 'bg-red-100 text-red-800', description: 'Immediate fine' },
    critical: { label: 'Critical', color: 'bg-red-200 text-red-900', description: 'Immediate action required' }
  };

  const violationStatuses = {
    reported: { label: 'Reported', color: 'bg-blue-100 text-blue-800' },
    first_notice: { label: 'First Notice', color: 'bg-yellow-100 text-yellow-800' },
    reminder_sent: { label: 'Reminder Sent', color: 'bg-orange-100 text-orange-800' },
    second_notice: { label: 'Second Notice', color: 'bg-red-100 text-red-800' },
    fine_issued: { label: 'Fine Issued', color: 'bg-red-200 text-red-900' },
    resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800' },
    escalated: { label: 'Escalated', color: 'bg-purple-100 text-purple-800' }
  };

  const isBoardMember = () => {
    return ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(user?.role);
  };

  useEffect(() => {
    if (user) {
      fetchViolations();
    }
  }, [user, filter]); // Refetch when filter changes
  
  // Auto-apply search and date filters with debouncing
  useEffect(() => {
    if (user) {
      const timer = setTimeout(() => {
        fetchViolations();
      }, 500); // 500ms debounce
      
      return () => clearTimeout(timer);
    }
  }, [searchQuery, monthFilter, upcomingPaymentFilter]);

  const fetchViolations = async () => {
    console.log('🔄 ViolationSystem - fetchViolations() called');
    try {
      setLoading(true);
      const currentHoaId = user?.hoa_id;
      console.log('🔍 Current HOA ID:', currentHoaId);
      
      if (!currentHoaId) {
        console.warn('⚠️ No HOA ID available');
        return;
      }
      
      // FIXED: Use correct endpoint format without HOA ID in path
      let url = '/api/violations';

      // Add filter parameters
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('status', filter);
      }
      
      // If not board member, only show own violations
      if (!isBoardMember()) {
        params.append('homeowner_id', user.id);
      }
      
      // Add search parameter
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      // Add month filter
      if (monthFilter) {
        params.append('reported_month', monthFilter);
      }
      
      // Add upcoming payments filter
      if (upcomingPaymentFilter) {
        params.append('upcoming_payments', upcomingPaymentFilter);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log('📡 Calling:', url);
      // FIXED: Add X-HOA-ID header
      const response = await api.get(url, {
        headers: { 'X-HOA-ID': currentHoaId }
      });
      console.log('✅ Violations response:', response.data);
      
      if (response.data) {
        const viols = response.data.violations || [];
        console.log(`📦 Violations returned: ${viols.length}`);
        if (viols.length > 0) {
          console.log('📋 First violation:', viols[0]);
        }
        setViolations(viols);
      } else {
        console.warn('⚠️ No data in response');
        setViolations([]);
      }
    } catch (error) {
      console.error('Error fetching violations:', error);
    } finally {
      setLoading(false);
    }
  };

  const createViolation = async (violationData) => {
    try {
      const currentHoaId = user?.hoa_id;
      if (!currentHoaId) return;
      
      const formData = new FormData();
      
      // Add all form fields
      Object.keys(violationData).forEach(key => {
        if (key === 'photos' || key === 'documentation') {
          // Handle file arrays
          violationData[key].forEach(file => {
            formData.append('proof_files', file);
          });
        } else {
          formData.append(key, violationData[key]);
        }
      });
      
      console.log('📡 Creating violation with HOA ID:', currentHoaId);
      console.log('📦 Violation data:', violationData);
      
      const response = await api.post('/api/violations', formData, {
        headers: {
          'X-HOA-ID': currentHoaId,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('✅ Violation created:', response.data);

      if (response.data) {
        alert('Violation reported successfully. Notification sent to homeowner.');
        setShowCreateModal(false);
        fetchViolations(); // Refresh list
      }
    } catch (error) {
      console.error('Error creating violation:', error);
      alert('Error creating violation. Please check your connection.');
    }
  };

  const fetchViolationDetails = async (violationId) => {
    try {
      const currentHoaId = user?.hoa_id;
      if (!currentHoaId) return null;
      
      const response = await api.get(`/api/violations/${currentHoaId}/${violationId}`, {
        headers: { 'X-HOA-ID': currentHoaId }
      });
      if (response.data) {
        return response.data.violation;
      }
    } catch (error) {
      console.error('Error fetching violation details:', error);
    }
    return null;
  };

  const addComment = async () => {
    if (!newComment.trim()) {
      alert('Please enter a comment.');
      return;
    }

    try {
      const currentHoaId = user?.hoa_id;
      if (!currentHoaId) return;
      
      setAddingComment(true);
      
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('comment', newComment);
      
      // Add any attachments
      commentAttachments.forEach(file => {
        formData.append('attachments', file);
      });
      
      const response = await api.put(`/api/violations/${selectedViolation.id}/add-comment`, formData, {
        headers: {
          'X-HOA-ID': currentHoaId
          // Don't set Content-Type - axios will set it automatically with boundary
        }
      });

      if (response.data) {
        // Refresh the violation details to show the new comment
        const detailedViolation = await fetchViolationDetails(selectedViolation.id);
        setSelectedViolation(detailedViolation);
        setNewComment('');
        setCommentAttachments([]);  // Clear attachments after sending
      } else {
        const errorData = await response.text();
        alert('Failed to add comment. Please try again.');
        console.error('Add comment error:', errorData);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment. Please check your connection.');
    } finally {
      setAddingComment(false);
    }
  };

  const handleCommentKeyPress = (e) => {
    // Only submit on Enter if Shift is not pressed (allows multiline with Shift+Enter)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addComment();
    }
  };

  const createViolationFine = async () => {
    if (!fineAmount || parseFloat(fineAmount) <= 0) {
      alert('Please enter a valid fine amount.');
      return;
    }

    try {
      const currentHoaId = user?.hoa_id;
      if (!currentHoaId) return;
      
      const response = await api.put(`/api/violations/${selectedViolation.id}/set-fine`, {
        amount: parseFloat(fineAmount)
      }, {
        headers: {
          'X-HOA-ID': currentHoaId
        }
      });

      if (response.data && response.data.success) {
        alert(`✅ Fine of $${fineAmount} created successfully!\n\n• Ledger entry created\n• Homeowner notified via email\n• Payable in Overview tab\n• Due date: ${new Date(response.data.due_date).toLocaleDateString()}`);
        setShowFineModal(false);
        setFineAmount('');
        
        // Refresh violations list to show updated fine information
        fetchViolations();
      } else {
        alert('⚠️ Fine may have been created but response was unexpected. Please check the violation and ledger.');
      }
    } catch (error) {
      console.error('Error creating fine:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`❌ Error creating fine: ${errorMessage}\n\nPlease check your connection and try again.`);
    }
  };

  const requestHearing = async (violation) => {
    try {
      const currentHoaId = user?.hoa_id;
      if (!currentHoaId) return;
      
      const response = await api.put(`/api/violations/${violation.id}/request-hearing`, {
        requested_by: user.id,
        requested_by_name: `${user.first_name} ${user.last_name}`,
        request_date: new Date().toISOString()
      });

      if (response.data) {
        alert('Hearing request submitted successfully!');
        fetchViolations(); // Refresh the list
      } else {
        const errorData = await response.text();
        alert('Failed to request hearing. Please try again.');
        console.error('Request hearing error:', errorData);
      }
    } catch (error) {
      console.error('Error requesting hearing:', error);
      alert('Error requesting hearing. Please check your connection.');
    }
  };

  const updateViolation = async (violationId, updateData) => {
    try {
      const currentHoaId = user?.hoa_id;
      if (!currentHoaId) return;
      
      const response = await api.put(`/api/violations/${violationId}`, updateData, {
        headers: { 'X-HOA-ID': currentHoaId }
      });

      if (response.data) {
        alert('Violation updated successfully.');
        fetchViolations(); // Refresh list
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error('Error updating violation:', error);
      alert('Error updating violation. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-300 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isBoardMember() ? 'Violation Management' : 'My Violations'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isBoardMember() 
                ? 'Manage community violations and compliance issues'
                : 'View your violation notices and compliance status'
              }
            </p>
          </div>
          
          {isBoardMember() && (
            <div className="flex gap-3">
              <button
                onClick={() => setShowSettingsModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <CogIcon className="h-4 w-4 mr-2" />
                Settings
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Report Violation
              </button>
            </div>
          )}
        </div>

        {/* Filter tabs */}
        <div className="mt-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'all', label: 'All Violations' },
              { key: 'reported', label: 'Active' },
              { key: 'fine_issued', label: 'Fine Issued' },
              { key: 'hearing_requested', label: 'Hearing Requested' },
              { key: 'resolved', label: 'Resolved' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  filter === tab.key
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Enhanced Search and Filtering Controls */}
        {isBoardMember() && (
          <div className="bg-gray-50 rounded-lg p-4 mt-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search Bar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search Violations</label>
                <input
                  type="text"
                  placeholder="Search by name, type, description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                />
              </div>
              
              {/* Month Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Month Reported</label>
                <select
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">All Months</option>
                  {(() => {
                    const months = [];
                    const now = new Date();
                    
                    // Generate last 24 months
                    for (let i = 0; i < 24; i++) {
                      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                      const value = date.toISOString().slice(0, 7); // YYYY-MM format
                      const label = date.toLocaleDateString('en-US', { 
                        month: 'long', 
                        year: 'numeric' 
                      });
                      months.push(
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    }
                    return months;
                  })()}
                </select>
              </div>
              
              {/* Upcoming Payments Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upcoming Payments</label>
                <select
                  value={upcomingPaymentFilter}
                  onChange={(e) => setUpcomingPaymentFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">All Violations</option>
                  <option value="week">Due This Week</option>
                  <option value="month">Due This Month</option>
                </select>
              </div>
              
              {/* Filter Actions */}
              <div className="flex items-end gap-2">
                <button
                  onClick={fetchViolations}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Apply Filters
                </button>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setMonthFilter('');
                    setUpcomingPaymentFilter('');
                    // Trigger a new fetch after clearing filters
                    setTimeout(fetchViolations, 100);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Clear
                </button>
              </div>
            </div>
            
            {/* Filter Summary */}
            {(searchQuery || monthFilter || upcomingPaymentFilter) && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm text-gray-600">Active filters:</span>
                  {searchQuery && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Search: "{searchQuery}"
                    </span>
                  )}
                  {monthFilter && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Month: {new Date(monthFilter + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                  )}
                  {upcomingPaymentFilter && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      Payments: Due {upcomingPaymentFilter === 'week' ? 'this week' : 'this month'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Violations List */}
      <div className="space-y-4">
        {violations.length === 0 ? (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No violations found</h3>
            <p className="text-gray-600">
              {filter === 'all' 
                ? 'No violations have been reported.'
                : `No violations with status "${filter}" found.`
              }
            </p>
          </div>
        ) : (
          violations.map(violation => (
            <ViolationCard
              key={violation.id}
              violation={violation}
              violationSeverities={violationSeverities}
              violationStatuses={violationStatuses}
              isBoardMember={isBoardMember()}
              onViewDetails={async (v) => {
                const detailedViolation = await fetchViolationDetails(v.id);
                setSelectedViolation(detailedViolation || v);
                setShowDetailModal(true);
              }}
              onUpdateStatus={(violationId, status) => updateViolation(violationId, { status })}
            />
          ))
        )}
      </div>

      {/* Create Violation Modal */}
      {showCreateModal && (
        <CreateViolationModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createViolation}
          violationTypes={violationTypes}
          violationSeverities={violationSeverities}
        />
      )}

      {/* Violation Details Modal */}
      {showDetailModal && selectedViolation && (
        <ViolationDetailModal
          violation={selectedViolation}
          violationSeverities={violationSeverities}
          violationStatuses={violationStatuses}
          isBoardMember={isBoardMember()}
          currentUser={user}
          onClose={() => setShowDetailModal(false)}
          onUpdate={updateViolation}
          newComment={newComment}
          setNewComment={setNewComment}
          commentAttachments={commentAttachments}
          setCommentAttachments={setCommentAttachments}
          addingComment={addingComment}
          onAddComment={addComment}
          onCommentKeyPress={handleCommentKeyPress}
          onRequestHearing={requestHearing}
          onSendFine={(violation) => {
            setSelectedViolation(violation);
            setShowDetailModal(false);
            setShowFineModal(true);
          }}
        />
      )}

      {/* Fine Creation Modal */}
      {showFineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Create Violation Fine</h3>
              <button 
                onClick={() => {
                  setShowFineModal(false);
                  setFineAmount('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">
                  <strong>Violation:</strong> {selectedViolation?.violation_type} at {selectedViolation?.property_address}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Severity:</strong> {selectedViolation?.severity?.toUpperCase()}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fine Amount ($) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={fineAmount}
                  onChange={(e) => setFineAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter fine amount"
                />
              </div>
              
              <div className="text-xs text-gray-500">
                <p>• Fine will be due in 30 days</p>
                <p>• Will appear in Payments tab as special assessment</p>
                <p>• When paid, will show as income in Budget & Finance</p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowFineModal(false);
                  setFineAmount('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createViolationFine}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Create Fine
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <ViolationSettings onClose={() => setShowSettingsModal(false)} />
      )}
    </div>
  );
};

// Individual Violation Card Component
const ViolationCard = ({ 
  violation, 
  violationSeverities, 
  violationStatuses, 
  isBoardMember, 
  onViewDetails, 
  onUpdateStatus 
}) => {
  const severityInfo = violationSeverities[violation.severity] || violationSeverities.minor;
  const statusInfo = violationStatuses[violation.status] || violationStatuses.reported;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilDue = () => {
    let dueDate = null;
    if (violation.status === 'first_notice' && violation.first_notice_due_date) {
      dueDate = new Date(violation.first_notice_due_date);
    } else if (violation.status === 'second_notice' && violation.second_notice_due_date) {
      dueDate = new Date(violation.second_notice_due_date);
    }
    
    if (dueDate) {
      const today = new Date();
      const diffTime = dueDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return null;
  };

  const daysUntilDue = getDaysUntilDue();

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${severityInfo.color}`}>
                {severityInfo.label}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
              
              {daysUntilDue !== null && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  daysUntilDue <= 0 ? 'bg-red-100 text-red-800' :
                  daysUntilDue <= 3 ? 'bg-orange-100 text-orange-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  <ClockIcon className="h-3 w-3 mr-1" />
                  {daysUntilDue <= 0 ? 'Overdue' : `${daysUntilDue} days left`}
                </span>
              )}
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {violation.violation_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Violation
            </h3>
            
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center">
                <HomeIcon className="h-4 w-4 mr-2" />
                <span>{violation.property_address}</span>
              </div>
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 mr-2" />
                <span>Reported: {formatDate(violation.reported_date)}</span>
              </div>
            </div>
            
            <p className="text-gray-700 mb-4 line-clamp-2">
              {violation.description}
            </p>
            
            {violation.fine_amount && (
              <div className="flex items-center text-red-600 mb-3">
                <BanknotesIcon className="h-4 w-4 mr-2" />
                <span className="font-medium">Fine: ${violation.fine_amount}</span>
              </div>
            )}
          </div>
          
          <div className="flex flex-col space-y-2 ml-4">
            <button
              onClick={() => onViewDetails(violation)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <EyeIcon className="h-4 w-4 mr-1" />
              Details
            </button>
            
            {isBoardMember && violation.status !== 'resolved' && (
              <div className="space-y-1">
                {violation.status === 'first_notice' && (
                  <button
                    onClick={() => onUpdateStatus(violation.id, 'resolved')}
                    className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100"
                  >
                    <CheckIcon className="h-4 w-4 mr-1" />
                    Resolve
                  </button>
                )}
                
                {violation.status === 'first_notice' && (
                  <button
                    onClick={() => onUpdateStatus(violation.id, 'second_notice')}
                    className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100"
                  >
                    2nd Notice
                  </button>
                )}
                
                {violation.status === 'second_notice' && (
                  <button
                    onClick={() => onUpdateStatus(violation.id, 'fine_issued')}
                    className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100"
                  >
                    Issue Fine
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Violation Modal
const CreateViolationModal = ({ onClose, onCreate, violationTypes, violationSeverities }) => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    homeowner_id: '',
    property_address: '',
    property_identifier: '',
    violation_type: 'other',
    description: '',
    severity: 'moderate',
    rule_cited: '',
    resolution_request: '',
    prior_meeting_id: '',
    photos: [],
    documentation: []
  });
  const [homeowners, setHomeowners] = useState([]);
  const [meetingMinutes, setMeetingMinutes] = useState([]);
  const [loadingHomeowners, setLoadingHomeowners] = useState(true);

  useEffect(() => {
    fetchHomeowners();
    fetchMeetingMinutes();
  }, []);

  const fetchMeetingMinutes = async () => {
    console.log('🔄 ViolationSystem - fetchMeetingMinutes() called');
    try {
      const currentHoaId = user?.hoa_id;
      console.log('🔍 HOA ID for meeting minutes:', currentHoaId);
      
      if (!currentHoaId) {
        console.warn('⚠️ No HOA ID available for meeting minutes');
        return;
      }
      
      console.log('📡 Calling meeting minutes API...');
      const response = await api.get(`/api/documents/meeting-minutes?hoa_code=${currentHoaId}`);
      console.log('✅ Meeting minutes response:', response.data);
      
      if (response.data) {
        setMeetingMinutes(response.data || []);
      }
    } catch (error) {
      console.error('❌ Error fetching meeting minutes:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const fetchHomeowners = async () => {
    console.log('🔄 ViolationSystem - fetchHomeowners() called');
    try {
      const currentHoaId = user?.hoa_id;
      console.log('🔍 Current HOA ID:', currentHoaId);
      
      if (!currentHoaId) {
        console.warn('⚠️ No HOA ID available');
        return;
      }
      
      console.log('📡 Calling /api/hoa/members...');
      const response = await api.get('/api/hoa/members');
      console.log('✅ Response received:', response);
      
      if (response.data) {
        const members = response.data.members || [];
        console.log(`📦 Members returned: ${members.length}`);
        
        // Format for dropdown
        const formattedMembers = members.map(m => ({
          id: m.id,
          name: m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email?.split('@')[0] || 'Unknown',
          email: m.email,
          property_address: m.property_address || m.address || '',
          address: m.property_address || m.address || '',
          property_identifier: m.property_identifier || ''
        }));
        
        console.log(`✅ Setting ${formattedMembers.length} homeowners`);
        setHomeowners(formattedMembers);
      }
    } catch (error) {
      console.error('❌ Error fetching homeowners:', error);
      console.error('Error details:', error.response?.data);
      setHomeowners([]);
    } finally {
      setLoadingHomeowners(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.homeowner_id || !formData.property_address || !formData.description) {
      alert('Please fill in all required fields.');
      return;
    }
    onCreate(formData);
  };

  const handleHomeownerSelect = (homeownerId) => {
    const selectedHomeowner = homeowners.find(h => h.id === homeownerId);
    setFormData({
      ...formData,
      homeowner_id: homeownerId,
      property_address: selectedHomeowner?.address || '',
      property_identifier: selectedHomeowner?.property_identifier || ''
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Report Violation</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Homeowner *
              </label>
              {loadingHomeowners ? (
                <div className="animate-pulse h-10 bg-gray-200 rounded"></div>
              ) : (
                <select
                  required
                  value={formData.homeowner_id}
                  onChange={(e) => handleHomeownerSelect(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Select homeowner</option>
                  {homeowners.map(homeowner => (
                    <option key={homeowner.id} value={homeowner.id}>
                      {homeowner.name} - {homeowner.address}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Address *
              </label>
              <input
                type="text"
                required
                value={formData.property_address}
                onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
                placeholder="123 Main St"
                readOnly={!!formData.homeowner_id}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Identifier
              </label>
              <input
                type="text"
                value={formData.property_identifier}
                onChange={(e) => setFormData({...formData, property_identifier: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Unit A1, Lot 5, etc."
                readOnly={!!formData.homeowner_id}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Violation Type *
              </label>
              <select
                required
                value={formData.violation_type}
                onChange={(e) => setFormData({...formData, violation_type: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
              >
                {violationTypes.map(type => (
                  <option key={type} value={type}>
                    {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Severity *
              </label>
              <select
                required
                value={formData.severity}
                onChange={(e) => setFormData({...formData, severity: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
              >
                {Object.entries(violationSeverities).map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.label} - {info.description}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Meeting Minutes Selection for Severe/Critical Violations */}
          {(formData.severity === 'severe' || formData.severity === 'critical') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link to Prior Meeting Minutes <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.prior_meeting_id}
                onChange={(e) => setFormData({...formData, prior_meeting_id: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
                required={formData.severity === 'severe' || formData.severity === 'critical'}
              >
                <option value="">Select meeting minutes...</option>
                {meetingMinutes.map(meeting => (
                  <option key={meeting.id} value={meeting.id}>
                    {meeting.title} - {new Date(meeting.created_at).toLocaleDateString()}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Required for severe/critical violations to provide board discussion context
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What is the Violation? *
            </label>
            <textarea
              required
              rows="3"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Describe the specific violation observed..."
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rule Cited
              </label>
              <input
                type="text"
                value={formData.rule_cited}
                onChange={(e) => setFormData({...formData, rule_cited: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
                placeholder="e.g., CCR Section 4.2, Bylaw Article V"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resolution Request
              </label>
              <input
                type="text"
                value={formData.resolution_request}
                onChange={(e) => setFormData({...formData, resolution_request: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
                placeholder="What action should be taken?"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photos/Documentation
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center mb-4">
                <PhotoIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">Upload photos or documents as evidence</p>
                <p className="text-xs text-gray-500">Accepted formats: JPG, HEIC (iPhone), PDF (Max 10MB each)</p>
                <p className="text-xs text-yellow-600 font-medium mt-1">⚠️ Note: PNG files are unreliable and may not display correctly. HEIC pictures from iPhone camera are recommended.</p>
              </div>
              
              <input
                type="file"
                multiple
                accept="image/*,application/pdf,.doc,.docx"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setFormData({
                    ...formData,
                    photos: [...formData.photos, ...files.filter(f => f.type.startsWith('image/'))],
                    documentation: [...formData.documentation, ...files.filter(f => !f.type.startsWith('image/'))]
                  });
                }}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
              />
              
              {/* Display uploaded files */}
              {(formData.photos.length > 0 || formData.documentation.length > 0) && (
                <div className="mt-4 space-y-2">
                  {formData.photos.map((file, index) => (
                    <div key={`photo-${index}`} className="flex items-center justify-between bg-green-50 p-2 rounded">
                      <span className="text-sm text-green-800">📸 {file.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const newPhotos = formData.photos.filter((_, i) => i !== index);
                          setFormData({...formData, photos: newPhotos});
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {formData.documentation.map((file, index) => (
                    <div key={`doc-${index}`} className="flex items-center justify-between bg-blue-50 p-2 rounded">
                      <span className="text-sm text-blue-800">📄 {file.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const newDocs = formData.documentation.filter((_, i) => i !== index);
                          setFormData({...formData, documentation: newDocs});
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
            >
              Report Violation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Violation Detail Modal
const ViolationDetailModal = ({ 
  violation, 
  violationSeverities, 
  violationStatuses, 
  isBoardMember, 
  currentUser,
  onClose, 
  onUpdate,
  newComment,
  setNewComment,
  commentAttachments,
  setCommentAttachments,
  addingComment,
  onAddComment,
  onCommentKeyPress,
  onRequestHearing,
  onSendFine
}) => {
  const [resolutionNotes, setResolutionNotes] = useState(violation.resolution_notes || '');
  
  // Fixed permission logic: homeowner can comment on their own violation
  const canComment = isBoardMember || (currentUser && violation && currentUser.id === violation.homeowner_id);
  const canRequestHearing = !violation.hearing_requested && (isBoardMember || (currentUser && violation && currentUser.id === violation.homeowner_id));

  // Document and meeting minutes handlers
  const handleDownloadDocument = async (docId) => {
    try {
      const response = await api.get(`/api/violations/document/${docId}/download`, {
        responseType: 'blob'
      });

      if (response.data) {
        const blob = response.data;
        const contentDisposition = response.headers['content-disposition'];
        let filename = `document_${docId}`;
        
        // Extract filename from content-disposition header if available
        if (contentDisposition) {
          // Try to match filename with quotes first, then without
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i) || 
                                contentDisposition.match(/filename=([^;\s]+)/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].trim();
          }
        }
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Document download failed:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const handleViewMeetingMinutes = async (meetingId) => {
    try {
      const response = await api.get(`/api/documents/${meetingId}/download`, {
        responseType: 'blob'
      });

      if (response.data) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `meeting_minutes_${meetingId}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Meeting minutes access failed:', error);
      alert('Failed to access meeting minutes. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Violation Details</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          {/* Status and Severity Badges */}
          <div className="flex gap-3 mt-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${violationSeverities[violation.severity]?.color}`}>
              {violation.severity?.toUpperCase()}
            </span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${violationStatuses[violation.status]?.color}`}>
              {violationStatuses[violation.status]?.label || violation.status?.toUpperCase()}
            </span>
          </div>
        </div>
        
        <div className="flex flex-col lg:flex-row h-full">
          {/* Left Section: Violation Information */}
          <div className="lg:w-1/2 p-6 border-r border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Violation Information</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Homeowner</dt>
                <dd className="text-sm text-gray-900">{violation.homeowner_name || 'Loading...'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Type</dt>
                <dd className="text-sm text-gray-900">
                  {violation.violation_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Property Address</dt>
                <dd className="text-sm text-gray-900">{violation.property_address}</dd>
              </div>
              {violation.property_identifier && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Property Identifier</dt>
                  <dd className="text-sm text-gray-900">{violation.property_identifier}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">What is the Violation?</dt>
                <dd className="text-sm text-gray-900">{violation.description}</dd>
              </div>
              {violation.rule_cited && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Rule Cited</dt>
                  <dd className="text-sm text-gray-900">{violation.rule_cited}</dd>
                </div>
              )}
              {violation.resolution_request && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Resolution Request</dt>
                  <dd className="text-sm text-gray-900">{violation.resolution_request}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Reported Date</dt>
                <dd className="text-sm text-gray-900">{new Date(violation.reported_date).toLocaleDateString()}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Reported By</dt>
                <dd className="text-sm text-gray-900">{violation.reported_by_name || 'Board Member'}</dd>
              </div>
              
              {/* Fine Information */}
              {violation.fine_amount && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Fine Amount</dt>
                  <dd className="text-sm text-gray-900 font-semibold text-red-600">
                    ${violation.fine_amount}
                    {violation.fine_due_date && (
                      <span className="text-xs text-gray-500 ml-2">
                        (Due: {new Date(violation.fine_due_date).toLocaleDateString()})
                      </span>
                    )}
                  </dd>
                </div>
              )}
              
              {/* Hearing Information */}
              {(violation.hearing_requested || violation.hearing_scheduled) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Hearing Status</dt>
                  <dd className="text-sm text-gray-900">
                    {violation.hearing_scheduled ? (
                      <div className="space-y-1">
                        <div className="text-blue-600 font-medium">
                          📅 Hearing Scheduled
                        </div>
                        <div className="text-sm text-gray-700">
                          <strong>Date:</strong> {violation.hearing_date ? new Date(violation.hearing_date).toLocaleDateString() : 'TBD'}
                          {violation.hearing_time && (
                            <span className="ml-2">
                              <strong>Time:</strong> {violation.hearing_time}
                            </span>
                          )}
                        </div>
                        {violation.hearing_location && (
                          <div className="text-sm text-gray-700">
                            <strong>Location:</strong> {violation.hearing_location}
                          </div>
                        )}
                        {violation.hearing_notes && (
                          <div className="text-sm text-gray-600 mt-2">
                            <strong>Notes:</strong> {violation.hearing_notes}
                          </div>
                        )}
                        {violation.hearing_rescheduled && (
                          <div className="text-xs text-orange-600 italic">
                            ⚠️ This hearing has been rescheduled
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-orange-600">Hearing Requested</span>
                    )}
                  </dd>
                </div>
              )}
            </dl>
            
            {/* Evidence & Documentation */}
            {(violation.proof_documents?.length > 0 || violation.prior_meeting_id) && (
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h4 className="text-md font-medium text-gray-800 mb-3">Evidence & Documentation</h4>
                
                {violation.proof_documents?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Proof Documents</p>
                    <div className="space-y-2">
                      {violation.proof_documents.map((docId, index) => (
                        <div key={docId} className="flex items-center p-2 bg-gray-50 rounded">
                          <DocumentTextIcon className="h-4 w-4 text-blue-500 mr-2" />
                          <span className="text-sm text-gray-700 flex-1">Evidence Document #{index + 1}</span>
                          <button 
                            onClick={() => handleDownloadDocument(docId)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {violation.prior_meeting_id && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Related Meeting Minutes</p>
                    <div className="flex items-center p-2 bg-blue-50 rounded">
                      <CalendarIcon className="h-4 w-4 text-blue-500 mr-2" />
                      <span className="text-sm text-blue-800 flex-1">Meeting Minutes Referenced</span>
                      <button 
                        onClick={() => handleViewMeetingMinutes(violation.prior_meeting_id)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        View Minutes
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Right Section: Comments and Actions */}
          <div className="lg:w-1/2 flex flex-col">
            {/* Comments Section */}
            <div className="flex-1 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Discussion & Comments</h3>
              
              {/* Comments Thread */}
              <div className="space-y-4 mb-4 max-h-64 overflow-y-auto">
                {violation.comments && violation.comments.length > 0 ? (
                  violation.comments.map((comment) => (
                    <div key={comment.id} className={`p-3 rounded-lg ${comment.is_board_member ? 'bg-blue-50 ml-4' : 'bg-gray-50 mr-4'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          {comment.user_name}
                          {comment.is_board_member && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Board
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(comment.timestamp).toLocaleDateString()} {new Date(comment.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{comment.comment}</p>
                      
                      {/* Display attachments if any */}
                      {comment.attachments && comment.attachments.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {comment.attachments.map((attachment, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <DocumentTextIcon className="h-3 w-3 text-gray-500" />
                              <button
                                onClick={() => handleDownloadDocument(attachment.id)}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                📎 {attachment.filename}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">💬</div>
                    <p className="text-sm">No comments yet. Start the discussion!</p>
                  </div>
                )}
              </div>
              
              {/* Add Comment */}
              {canComment && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add a comment
                  </label>
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <textarea
                          rows="3"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          onKeyPress={onCommentKeyPress}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Type your comment... (Press Enter to send, Shift+Enter for new line)"
                          disabled={addingComment}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Press Enter to send • Shift+Enter for new line
                        </p>
                      </div>
                      <button
                        onClick={onAddComment}
                        disabled={addingComment || !newComment.trim()}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed h-fit"
                      >
                        {addingComment ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                    
                    {/* File attachment option */}
                    <div>
                      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md border border-blue-200">
                        <DocumentTextIcon className="h-4 w-4" />
                        <span>Attach Files</span>
                        <input
                          type="file"
                          multiple
                          accept="image/*,application/pdf,.doc,.docx"
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            setCommentAttachments([...commentAttachments, ...files]);
                          }}
                          className="hidden"
                          disabled={addingComment}
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">HEIC (iPhone), JPG, PDF accepted. PNG files may not display correctly.</p>
                      
                      {/* Display attached files */}
                      {commentAttachments.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {commentAttachments.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                              <span className="text-gray-700">📎 {file.name}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setCommentAttachments(commentAttachments.filter((_, i) => i !== index));
                                }}
                                className="text-red-500 hover:text-red-700 text-xs"
                                disabled={addingComment}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Actions Available to Homeowner and Board */}
            {canRequestHearing && (
              <div className="border-t border-gray-200 p-6 bg-yellow-50">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Request Hearing</h3>
                <p className="text-sm text-gray-600 mb-4">
                  If you disagree with this violation or wish to contest it, you may request a hearing with the board.
                </p>
                <button
                  onClick={() => {
                    if (window.confirm('Request a hearing for this violation? This will pause any payment due dates until the hearing is completed.')) {
                      onRequestHearing(violation);
                    }
                  }}
                  className="w-full px-4 py-2 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700"
                >
                  🏛️ Request Hearing
                </button>
              </div>
            )}
            
            {/* Board Actions Section */}
            {isBoardMember && violation.status !== 'resolved' && (
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Board Actions</h3>
                
                <div className="space-y-4">
                  {/* Send Fine Button */}
                  {violation.status !== 'fine_issued' && !violation.fine_amount && (
                    <button
                      onClick={() => onSendFine(violation)}
                      className="w-full px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 mb-3"
                    >
                      💰 Send Fine
                    </button>
                  )}
                  
                  {/* Schedule Hearing - always available for board members */}
                  {!violation.hearing_scheduled && (
                    <button
                      onClick={async () => {
                        const hearingDate = prompt('Enter hearing date (YYYY-MM-DD):');
                        const hearingTime = prompt('Enter hearing time (HH:MM):') || '';
                        const hearingLocation = prompt('Enter hearing location:') || '';
                        
                        if (hearingDate) {
                          try {
                            const currentHoaId = currentUser?.hoa_id;
                            if (!currentHoaId) return;
                            
                            const response = await api.put(`/api/violations/${violation.id}/schedule-hearing`, {
                              hearing_date: hearingDate,
                              hearing_time: hearingTime,
                              hearing_location: hearingLocation,
                              notes: `Hearing scheduled by ${currentUser.first_name} ${currentUser.last_name}`
                            });
                            
                            if (response.data) {
                              alert(`Hearing scheduled successfully for ${hearingDate}${response.data.payments_paused ? '. Payment reminders have been paused.' : ''}`);
                              onClose();
                              // Parent component will refresh violations
                            }
                          } catch (error) {
                            console.error('Error scheduling hearing:', error);
                            alert('Error scheduling hearing. Please try again.');
                          }
                        }
                      }}
                      className="w-full px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 mb-3"
                    >
                      📅 Schedule Hearing
                    </button>
                  )}

                  {/* Reschedule Hearing for already scheduled hearings */}
                  {violation.hearing_scheduled && (
                    <button
                      onClick={async () => {
                        const confirmReschedule = window.confirm(
                          'Reschedule this hearing?\n\n⚠️ STATE LAW NOTICE: Rescheduling may require additional waiting period for proper advance notice to homeowner per state regulations.\n\nDo you want to proceed with rescheduling?'
                        );
                        
                        if (confirmReschedule) {
                          const newHearingDate = prompt('Enter new hearing date (YYYY-MM-DD):');
                          const newHearingTime = prompt('Enter new hearing time (HH:MM):') || '';
                          const newHearingLocation = prompt('Enter new hearing location:') || '';
                          const rescheduleReason = prompt('Enter reason for rescheduling:') || '';
                          
                          if (newHearingDate) {
                            try {
                              const currentHoaId = currentUser?.hoa_id;
                              if (!currentHoaId) return;
                              
                              const response = await api.put(`/api/violations/${violation.id}/reschedule-hearing`, {
                                hearing_date: newHearingDate,
                                hearing_time: newHearingTime,
                                hearing_location: newHearingLocation,
                                reschedule_reason: rescheduleReason
                              });
                              
                              if (response.data) {
                                alert(`Hearing rescheduled successfully to ${response.data.new_hearing_date}. Email notification sent to homeowner.`);
                                onClose();
                                // Parent component will refresh violations
                              }
                            } catch (error) {
                              console.error('Error rescheduling hearing:', error);
                              alert('Error rescheduling hearing. Please try again.');
                            }
                          }
                        }
                      }}
                      className="w-full px-4 py-2 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 mb-3"
                    >
                      📅 Reschedule Hearing
                    </button>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Resolution Notes
                    </label>
                    <textarea
                      rows="3"
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      placeholder="Enter resolution details..."
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        if (window.confirm('Mark this violation as resolved?')) {
                          onUpdate(violation.id, { status: 'resolved', resolution_notes: resolutionNotes });
                        }
                      }}
                      className="flex-1 px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                    >
                      ✓ Resolve Violation
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViolationSystem;